// Components==============
import { faUserPlus } from '@fortawesome/pro-solid-svg-icons';
import AddUserModal from 'components/UI/AddUserModal/AddUserModal';
import CircleButton from 'components/UI/CircleButton/CircleButton';
import ReflectionAvatar from 'components/UI/ReflectionAvatar/ReflectionAvatar';
import { TooltipContext } from 'components/UI/Tooltip/TooltipContextWrapper';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslate } from 'hooks/useTranslate';
import { ProjectDetailsContext } from 'pages/Workspace/ProjectDetails/ProjectDetails';
import React, { useContext, useState } from 'react';
import './MultipleUsers.module.scss';
// =========================

const variants = {
  visible: { x: 0 },
  invisible: (index: number) => ({ x: 33 * index, zIndex: index }),
};

const variantChild = {
  visible: { opacity: 1 },
  invisible: { opacity: 0 },
};

const variantsLargeGroupWrapper = {
  visible: {
    transition: { staggerChildren: 0.1 },
  },
  invisible: {},
};

export default function MultipleUsers({
  process,
}: {
  process: ProcessProcessEntity;
}) {
  const [hovering, setHovering] = useState<boolean>(false);
  const { project, processGroups, handleProcessSave } = useContext(
    ProjectDetailsContext
  );

  const { setTooltipIsVisible } = useContext(TooltipContext);

  const translate = useTranslate();

  const avatars =
    process?.users
      ?.map(
        (uId) =>
          project?.members?.find((member) => member.user_id === uId)?.user
      )
      .filter((exists) => exists) || [];

  const smallGroup = avatars.slice(0, 3);
  const largeGroup = avatars.slice(3, avatars.length);

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const message = (user: UserEntity) =>
    process.users?.includes(user.id)
      ? `  <b>(${translate('reflection.already-invited')})</b>`
      : '';

  function handleAddUser(userIds: number[]) {
    const newValue = processGroups?.map((group) => {
      if (
        group.activities.find(
          (activity) =>
            activity.processId === process?.id &&
            (activity.process_type === process?.process_type) !== undefined
        )
      )
        return {
          ...group,
          activities: group.activities.map((activity) =>
            activity.processId === process?.id
              ? {
                  ...activity,
                  users: activity.users?.length
                    ? [...activity.users, ...userIds]
                    : [...userIds],
                }
              : activity
          ),
        };

      return group;
    });

    handleProcessSave(newValue);
  }

  function handleDelete(id: number) {
    setTooltipIsVisible(false);

    const newValue = processGroups?.map((group) => {
      if (
        group.activities.find(
          (activity) =>
            activity.processId === process?.id &&
            (activity.process_type === process?.process_type) !== undefined
        )
      )
        return {
          ...group,
          activities: group.activities.map((activity) =>
            activity.processId === process?.id
              ? {
                  ...activity,
                  users: activity.users?.filter((u) => u !== id),
                }
              : activity
          ),
        };

      return group;
    });

    handleProcessSave(newValue);
  }

  return (
    <>
      {avatars.length ? (
        <div styleName="relative">
          <div styleName={`container ${hovering ? 'hovering' : ''}`}>
            <motion.div
              onMouseEnter={() => {
                setHovering(true);
              }}
              onMouseLeave={() => {
                setHovering(false);
              }}
              styleName={`avatar-wrapper ${
                largeGroup.length ? 'large-group' : ''
              }`}
            >
              {!!(largeGroup.length && hovering) && (
                <motion.div
                  initial="invisible"
                  exit="invisible"
                  variants={variantsLargeGroupWrapper}
                  animate={hovering ? 'visible' : 'invisible'}
                  styleName="large-group"
                >
                  {largeGroup.map(
                    (avatar) =>
                      !!avatar && (
                        <motion.div
                          key={avatar?.id}
                          styleName="avatar"
                          variants={variantChild}
                          custom={1}
                        >
                          <ReflectionAvatar
                            user={avatar}
                            onClick={() => handleDelete(avatar.id)}
                            canDelete
                          />
                        </motion.div>
                      )
                  )}
                </motion.div>
              )}
              {smallGroup.map(
                (avatar, i) =>
                  !!avatar && (
                    <motion.div
                      key={avatar?.id}
                      styleName="avatar"
                      variants={variants}
                      initial="invisible"
                      animate={hovering ? 'visible' : 'invisible'}
                      custom={smallGroup.length - i}
                    >
                      <ReflectionAvatar
                        user={avatar}
                        onClick={() => handleDelete(avatar.id)}
                        canDelete
                      />
                    </motion.div>
                  )
              )}
              <div styleName="avatar">
                <CircleButton
                  icon={faUserPlus}
                  color="dark-grey"
                  onClick={() => setModalIsOpen(true)}
                />
              </div>
            </motion.div>
          </div>
        </div>
      ) : (
        <CircleButton
          icon={faUserPlus}
          color="dark-grey"
          onClick={() => setModalIsOpen(true)}
        />
      )}
      <AnimatePresence>
        {modalIsOpen && (
          <AddUserModal
            title={translate('project.task-division-modal.title')}
            description={translate('project.task-division-modal.description')}
            onSubmit={handleAddUser}
            setModalIsOpen={setModalIsOpen}
            message={message}
            includeSelf
            includeExtern
            type="feedback"
            searchFilter={(user: UserEntity) =>
              project?.members.find(
                (member) =>
                  project.author?.id === user.id || member.user_id === user.id
              ) !== undefined
            }
          />
        )}
      </AnimatePresence>
    </>
  );
}
